import type React from 'react'

/**
 * Stable identifier for each kind of section the client records PDF is assembled from.
 *
 * Section *names* embed the episode and session (e.g. "Client Assignments for Episode 2 -
 * Session 3"), so they can't be matched against a fixed list. These keys can.
 */
export type RecordsPdfSectionKey =
  | 'tableOfContents'
  | 'clientDetails'
  | 'benefitsSummary'
  | 'sessions'
  | 'notes'
  | 'progress'
  | 'assignments'
  | 'careConsents'
  | 'medications'

/**
 * Sections that never receive automatic redaction suggestions (LCDELIVERY-7915).
 *
 * These documents are boilerplate with no client-specific detail, so every suggestion the
 * keyword scanner raises on them is noise the Records team has to deny by hand. Their pages
 * still appear in the PDF and can still be redacted manually - only the automatic pass skips
 * them, and nothing in the UI calls this out.
 *
 * To exclude another section, add its key here. If the section doesn't have its own key yet,
 * give it one above and tag it in ClientRecordsExportPDF so it lands in its own PDF document -
 * exclusion works on page ranges, so excluded content must not share pages with included content.
 */
export const RECORDS_PDF_REDACTION_EXCLUDED_SECTION_KEYS: readonly RecordsPdfSectionKey[] = [
  'benefitsSummary',
  'careConsents',
]

export const isRecordsPdfRedactionExcludedSection = (sectionKey: RecordsPdfSectionKey): boolean =>
  RECORDS_PDF_REDACTION_EXCLUDED_SECTION_KEYS.includes(sectionKey)

/**
 * Maps excluded sections onto their 1-based page numbers in the merged PDF.
 *
 * Sections are rendered separately and concatenated in order, so a section's pages are simply
 * the next `pageCount` pages after everything before it.
 */
export const getRecordsPdfRedactionExcludedPageNumbers = (
  sections: readonly { sectionKey: RecordsPdfSectionKey; pageCount: number }[],
): number[] => {
  const excludedPageNumbers: number[] = []
  let pagesSoFar = 0

  sections.forEach(({ sectionKey, pageCount }) => {
    if (isRecordsPdfRedactionExcludedSection(sectionKey)) {
      for (let offset = 0; offset < pageCount; offset += 1) {
        excludedPageNumbers.push(pagesSoFar + offset + 1)
      }
    }
    pagesSoFar += pageCount
  })

  return excludedPageNumbers
}

/** One rendered assignments group, tagged so care consents can be excluded from redaction. */
export type RecordsPdfAssignmentsSession = {
  sessionName: string
  content: React.ReactNode
  sectionKey: Extract<RecordsPdfSectionKey, 'assignments' | 'careConsents'>
}

export type RecordsPdfSection = {
  sectionKey: RecordsPdfSectionKey
  sectionName: string
  content: React.ReactElement
}
